import { ReactElement, useEffect, useState } from "react";
import { Typography, Box, CircularProgress, IconButton } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { useSelector } from "react-redux";
import { LoanService } from "services/LoanService";
import { DefaultPagination } from "services";
import Utils from "shared/helpers/Utils";
import { saveAs } from "file-saver";
import DocumentPreview from "./DocumentPreview";
import EmptyPlaceholder from "shared/components/EmptyPlaceholder";
import {
  getDocumentStatusColorCode,
  UploadedDocumentsProps,
} from "./DocumentUpload";
import { BlackTooltip } from "shared/components/AppComponents";
import { DocumentStatusCode } from "shared/helpers/Constants";
import { useProgress } from "context/ProgressProvider";

const DocumentsToDownload = (): ReactElement => {
  const { currentProfile } = useProgress();

  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(
    {} as UploadedDocumentsProps
  );
  const [pagination, setPagination] = useState({
    ...DefaultPagination(20),
  });
  const [loanDocumentsList, setLoanDocumentsList] = useState<
    UploadedDocumentsProps[]
  >([]);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);

  // API Calls
  const loadDocumentsList = (): void => {
    setLoadingHistory(true);
    LoanService.getAllMappedDocuments(
      pagination.offset,
      pagination.limit,
      currentProfile.loadId
    )
      .then((response: any) => {
        const { documents, pagination } = response.data.data;
        setLoanDocumentsList(documents);
        setPagination(pagination);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setLoadingHistory(false);
      });
  };

  const downloadSignedDocuments = (data, callback) => {
    LoanService.downloadSignedDocuments(data)
      .then((response: any) => {
        callback(true);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
        callback(true);
      })
      .finally(() => {});
  };

  // UI Actions
  const actionOnView = (document: UploadedDocumentsProps): void => {
    setSelectedDocument(document);
    setShowDocumentPreview(true);
  };

  const actionOnDowload = async (
    document: UploadedDocumentsProps
  ): Promise<void> => {
    setIsDownloading(true);
    setSelectedDocument(document);
    const requestPayload = {
      loanId: document.loanId,
      loanDocumentId: document.loanDocumentId,
    };
    downloadSignedDocuments(requestPayload, async () => {
      await saveAs(
        document.blobFilePathToDownload,
        `${document?.documents?.documentName}.pdf`
      );
      setTimeout(() => {
        setSelectedDocument({} as UploadedDocumentsProps);
        setIsDownloading(false);
      }, 1000);
    });
  };

  const onCloseView = (): void => {
    setShowDocumentPreview(false);
    loadDocumentsList();
  };

  useEffect(() => {
    loadDocumentsList();
  }, []);

  useEffect(() => {
    //
  }, []);

  // UI Elements
  const filteredDocuments = loanDocumentsList.filter(
    (loan) => loan?.documents?.isAdminUploadForSign
  );

  console.log("filteredDocuments", filteredDocuments);

  return (
    <>
      {loadingHistory && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!loadingHistory && filteredDocuments.length > 0 && (
        <>
          <Box className="app-box-wrapper mt-5">
            <Box className="app-box-header header flex-column">
              <Typography variant="h2" className="f17">
                Download Documents
              </Typography>
            </Box>
            {filteredDocuments.map((loanDocument, index) => (
              <Box
                className={`document-upload-wrapper px-5 pt-5 ${
                  index + 1 === filteredDocuments.length && "pb-5"
                }`}
              >
                <Box className="document-upload-section py-3">
                  <Box
                    className="header justify-space-between flex"
                    sx={{
                      flexDirection: { xs: "column", md: "unset" },
                    }}
                  >
                    <Box className="flex align-center">
                      <Box className="upload-file-icon">
                        <img
                          alt="Mail"
                          src="/icons/icon_file.png"
                          width={20}
                          height={20}
                        />
                      </Box>
                      <Box className="flex-column ml-3">
                        <Typography variant="h3" className="title">
                          {loanDocument?.documents?.documentName}
                        </Typography>
                        {/* <Typography variant="body1" className="description">
                          {loanDocument?.documents?.documentDescription}
                        </Typography> */}
                      </Box>
                    </Box>

                    <Box className="justify-end align-center">
                      <>
                        {loanDocument.documentStatus !==
                          DocumentStatusCode.REQUESTED && (
                          <>
                            {getDocumentStatusColorCode(
                              loanDocument.documentStatus
                            )}
                          </>
                        )}
                        {loanDocument.blobFilePathToDownload !== null && (
                          <>
                            <BlackTooltip title="Download">
                              <IconButton
                                onClick={() => {
                                  setSelectedDocument(loanDocument);
                                  actionOnDowload(loanDocument);
                                }}
                                className="ml-2 icon-button"
                                disabled={isDownloading}
                              >
                                {isDownloading &&
                                selectedDocument.loanDocumentId ===
                                  loanDocument.loanDocumentId ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <img
                                    alt="Download"
                                    src="/icons/icon_download.png"
                                  />
                                )}
                              </IconButton>
                            </BlackTooltip>

                            <BlackTooltip title="View">
                              <IconButton
                                className="ml-2"
                                onClick={() => actionOnView(loanDocument)}
                              >
                                <RemoveRedEyeIcon />
                              </IconButton>
                            </BlackTooltip>
                          </>
                        )}
                      </>
                    </Box>
                  </Box>

                  {loanDocument?.documents?.instructions !== null && (
                    <Box className="upload-footer-section mt-3">
                      <Box className="info-header flex align-center">
                        <img
                          alt="File"
                          src="/icons/icon_info.png"
                          width={20}
                          height={20}
                        />
                        <Typography variant="h3" className="ml-2">
                          Important Instructions
                        </Typography>
                      </Box>
                      <Box className="important-note">
                        {!loanDocument?.isDownloaded && (
                          <>
                            {loanDocument?.documents?.instructions.length > 0 &&
                              loanDocument?.documents?.instructions.map(
                                (note: string) => (
                                  <ul className="list">
                                    <li>
                                      <Typography
                                        variant="body1"
                                        className="note"
                                      >
                                        {note}
                                      </Typography>
                                    </li>
                                  </ul>
                                )
                              )}
                          </>
                        )}

                        {loanDocument?.isDownloaded && (
                          <>
                            {loanDocument?.documents?.instructionsForDownload
                              .length > 0 &&
                              loanDocument?.documents?.instructionsForDownload.map(
                                (note: string) => (
                                  <ul className="list">
                                    <li>
                                      <Typography
                                        variant="body1"
                                        className="note"
                                      >
                                        {note}
                                      </Typography>
                                    </li>
                                  </ul>
                                )
                              )}
                          </>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          {showDocumentPreview && (
            <DocumentPreview
              showPdfViewer={showDocumentPreview}
              onCloseDocument={onCloseView}
              activeDocument={selectedDocument}
              loadDocumentsList={loadDocumentsList}
            />
          )}
        </>
      )}

      {!loadingHistory && filteredDocuments.length === 0 && (
        <EmptyPlaceholder message="No documents found" />
      )}
    </>
  );
};

export default DocumentsToDownload;
